import { createSlice } from '@reduxjs/toolkit'
import { UPDATE_APP_DATA } from 'constants/action_types'
import {
  START_TRIAL_STARTED,
  START_TRIAL_SUCCESS,
  UPDATE_SUBSCRIPTION_STARTED,
  UPDATE_SUBSCRIPTION_SUCCESS,
  UPDATE_SUBSCRIPTION_FAILED,
  CANCEL_SUBSCRIPTION_STARTED,
  CANCEL_SUBSCRIPTION_SUCCESS,
  FETCH_BILLING_DATA_V1_REQUEST,
  FETCH_BILLING_DATA_V1_SUCCESS,
  FETCH_BILLING_DATA_V1_FAILED,
  APP_BILLING_EXPIRED,
  FETCH_BILLING_INVOICES_STARTED,
  FETCH_BILLING_INVOICES_SUCCESS,
  FETCH_BILLING_INVOICES_FAILED,
  FETCH_BILLING_INFORMATION_REQUEST,
  FETCH_BILLING_INFORMATION_SUCCESS,
  FETCH_BILLING_INFORMATION_FAILED,
  UPDATE_BILLING_INFORMATION_SUCCESS,
  UPDATE_BILLING_INFORMATION_FAILED,
  FETCH_BILLING_SUCCESS,
  CANCEL_DOWNGRADE_STARTED,
  CANCEL_DOWNGRADE_SUCCESS,
  CANCEL_DOWNGRADE_FAILED,
  GET_INVOICE_PREVIEW_STARTED,
  GET_INVOICE_PREVIEW_SUCCESS,
  GET_INVOICE_PREVIEW_FAILED,
} from './types'

// Flat billing state (default {}). Entity/normalization coupling lives in the
// selectors (ducks/entities), NOT this reducer, so createSlice conversion is
// self-contained. Action strings preserved via extraReducers on imported
// constants; types.js/operations/selectors/store untouched.

// Shared: UPDATE_APP_DATA / FETCH_BILLING_SUCCESS / APP_BILLING_EXPIRED.
// No-op (returns original state) when action.data.billing is absent.
const applyAppData = (draftState, action) => {
  const { billing, creditCards, plans } = action.data
  if (!billing) return
  Object.assign(draftState, billing, {
    cards: creditCards,
    isLoading: false,
    isLoaded: true,
    plans,
  })
}

const setLoadingTrue = draftState => {
  draftState.isLoading = true
}

const setLoadingFalse = draftState => {
  draftState.isLoading = false
}

const setInformationError = (draftState, action) => {
  draftState.information = {
    isLoading: false,
    hasError: true,
    error: action.payload.error,
  }
}

const billing = createSlice({
  name: 'billing',
  initialState: {},
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(UPDATE_APP_DATA, applyAppData)
      .addCase(FETCH_BILLING_SUCCESS, applyAppData)
      .addCase(APP_BILLING_EXPIRED, applyAppData)
      .addCase(FETCH_BILLING_DATA_V1_REQUEST, setLoadingTrue)
      .addCase(CANCEL_DOWNGRADE_STARTED, setLoadingTrue)
      .addCase(GET_INVOICE_PREVIEW_STARTED, setLoadingTrue)
      .addCase(FETCH_BILLING_DATA_V1_SUCCESS, (draftState, action) => {
        const { billing: data, creditCards } = action.payload
        Object.assign(draftState, data, {
          cards: creditCards,
          isLoading: false,
          isLoaded: true,
        })
      })
      .addCase(GET_INVOICE_PREVIEW_SUCCESS, (draftState, action) => {
        if (!draftState.account) draftState.account = {}
        draftState.account.upcomingInvoice = action.payload.invoicePreview
        draftState.isLoading = false
        draftState.isLoaded = true
      })
      .addCase(FETCH_BILLING_DATA_V1_FAILED, (draftState, action) => {
        draftState.isLoading = false
        draftState.billingDataError = action.payload.error
      })
      .addCase(CANCEL_DOWNGRADE_FAILED, setLoadingFalse)
      .addCase(CANCEL_DOWNGRADE_SUCCESS, setLoadingFalse)
      .addCase(GET_INVOICE_PREVIEW_FAILED, setLoadingFalse)
      .addCase(START_TRIAL_STARTED, (draftState, action) => {
        Object.assign(draftState, action.payload, { isStartingTrial: true })
      })
      .addCase(START_TRIAL_SUCCESS, (draftState, action) => {
        Object.assign(draftState, action.payload, { isStartingTrial: false })
      })
      .addCase(UPDATE_SUBSCRIPTION_STARTED, draftState => {
        draftState.isSaving = true
        draftState.updateSubscriptionError = null
      })
      .addCase(UPDATE_SUBSCRIPTION_SUCCESS, (draftState, action) => {
        Object.assign(draftState, action.payload.updateSubscription, {
          isSaving: false,
        })
      })
      .addCase(UPDATE_SUBSCRIPTION_FAILED, (draftState, action) => {
        draftState.isSaving = false
        draftState.updateSubscriptionError = action.payload.error
      })
      .addCase(CANCEL_SUBSCRIPTION_STARTED, draftState => {
        draftState.isCanceling = true
      })
      .addCase(CANCEL_SUBSCRIPTION_SUCCESS, (draftState, action) => {
        Object.assign(draftState, action.payload.cancelSubscription, {
          isCanceling: false,
        })
      })
      .addCase(FETCH_BILLING_INVOICES_STARTED, draftState => {
        draftState.invoices = { isLoading: true, hasError: false }
      })
      .addCase(FETCH_BILLING_INVOICES_SUCCESS, (draftState, action) => {
        draftState.invoices = {
          isLoading: false,
          isLoaded: true,
          data: action.payload,
        }
      })
      .addCase(FETCH_BILLING_INVOICES_FAILED, (draftState, action) => {
        draftState.invoices = {
          isLoading: false,
          hasError: true,
          error: action.payload.error,
        }
      })
      .addCase(FETCH_BILLING_INFORMATION_REQUEST, draftState => {
        const isLoaded = !draftState.information
          ? false
          : !!draftState.information.isLoaded
        draftState.information = { isLoading: true, isLoaded, hasError: false }
      })
      .addCase(FETCH_BILLING_INFORMATION_SUCCESS, (draftState, action) => {
        draftState.information = {
          isLoading: false,
          isLoaded: true,
          data: action.payload.data.billingInformation,
        }
      })
      .addCase(UPDATE_BILLING_INFORMATION_SUCCESS, (draftState, action) => {
        draftState.information = {
          data: action.payload.updateBillingInformation,
        }
      })
      .addCase(FETCH_BILLING_INFORMATION_FAILED, setInformationError)
      .addCase(UPDATE_BILLING_INFORMATION_FAILED, setInformationError)
  },
})

export default billing.reducer
